import { appContext } from '../../../../AppContext';
import { Typography, Box, SvgIcon, Divider, FormControl, FormLabel, FormControlLabel, RadioGroup, Radio, CircularProgress, TextField, Checkbox } from '@mui/material';
import { DateTime } from 'luxon';
import dayjs, { Dayjs } from 'dayjs';
import { ReactComponent as SpacesIcon } from '../../../../Components/Layout/Sidebar/icons/Spaces.svg';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import { IMeetingSpace } from './BookingCriteria';
import { SpacesFilter } from '../../../../Providers.Api/Spaces/SpaceRepository';
import IbssSvgIcon from '../../../../Components/Icons/SvgIcon/IbssSvgIcon';
import { Icons } from '../../../../Common/AllsvgIcons';
import IbssDatePicker from '../../../../Components/Inputs/DatePicker/IbssDatePicker';
import IbssTimePicker from '../../../../Components/Inputs/TimePicker/IbssTimePicker';
import { DateHelper } from '../../../../Common/DateHelper';
import { IBookingSlots } from '../../../../Providers.Api/BookingPolicies/BookingPolicyRepository';
import { CateringOrdersFilter } from '../../../../Providers.Api/CateringOrders/CateringOrderRepository';
import { IPatchCateringOrder } from '../../../../Providers.Api/CateringOrders/PatchCateringOrderEndpoint';
import { IRootNode } from '../../../../Providers.Api/Models';
import { BookingSlotHelper } from '../../../../Common/BookingSlotHelper';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { SpacePicker } from '../../../../Components/Lists/SpacePicker/SpacePicker';
import { TypeHelper } from '../../../../Common/TypeHelper';
import ChangeBookingDetailsDialog, { IBookingDetailsApplication } from '../../../../Components/Dialogs/ChangeBookingDetailsDialog/ChangeBookingDetailsDialog';
import { BuildingParamater } from './DataModels';

class SwapDateTime extends IbssComponent<IProps, IState>
{
    private get api() { return appContext().apiClient; }
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private get hasV2Rights() { return appContext().localStorageProvider.hasRight("API.Bookings.V2"); }
    private nodes: IRootNode;
    private originalBookingStart: Date;
    private originalBookingEnd: Date;

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            bookingDate: new Date(),
            start: new Date(),
            end: new Date(),
            bookingSlotsPolicy: this.props.bookingSlotsPolicy,
            startIntervalMinutes: [],
            endIntervalMinutes: [],
            isStartValid: true,
            isEndValid: true,
            timeDifference: '',
            isLoading: false,
            selectedSpaceId: '',
            selectedSpaceHasCatering: false,
            selectedSpaceBookingPolicyId: '',
            bookedEquipment: [],
            moveResources: true,
            showChangeBookingDetailsPopup: false,
            showSpacesResults: false,
            buildingAlwaysApproveCateringOrders: 0
        };

        this.nodes = this.local.getNodeData()
        this.originalBookingStart = new Date();
        this.originalBookingEnd = new Date();
    }

    public async componentDidMount(): Promise<void>
    {
        //set start and end time for date & time pickers
        const startTime = DateTime.fromISO(this.props.bookingStart);
        const endTime = DateTime.fromISO(this.props.bookingEnd);
        //set initial bookingStart time
        this.originalBookingStart = startTime.toJSDate();
        this.originalBookingEnd = endTime.toJSDate();

        const buildingParams = await appContext().ibssApiClientV1.v1.byNodeid.parameters.get<BuildingParamater[]>({
            nodeId: this.props.buildingId,
            filter: `Node_Id eq ${this.props.buildingId} and Parameter_Name eq 'AlwaysApproveCateringOrders'`,
            select: BuildingParamater
        });

        let alwaysApproveCateringOrders = '0'

        if (buildingParams.length > 0)
        {
            alwaysApproveCateringOrders = buildingParams[0].Parameter_Value;
        }

        this.setState({
            // Date interface takes monthIndex, rather than month. It is 0 indexed. e.g feb is represented with 1.
            start: startTime.toJSDate(),
            end: endTime.toJSDate(),
            bookingDate: startTime.toJSDate(),
            buildingAlwaysApproveCateringOrders: parseInt(alwaysApproveCateringOrders)
        });
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState): Promise<void>
    {
        // if user selects a new SpaceId, get space's booking policy and set bookingSlotsPolicy, startIntervalMinutes & endIntervalMinutes
        if (prevState.selectedSpaceId !== this.state.selectedSpaceId)
        {
            const policyId = this.state.selectedSpaceBookingPolicyId;
            if (policyId && policyId !== '0')
            {
                const bookingPolicy = await this.api.bookingPolicies.getBookingPolicy(this.props.buildingId, policyId);
                if (bookingPolicy == null)
                {
                    return;
                }

                this.setState({
                    startIntervalMinutes: JSON.parse(bookingPolicy.Booking_Policy).BookingSlots.BookingStart.SpecificMinutes,
                    endIntervalMinutes: JSON.parse(bookingPolicy.Booking_Policy).BookingSlots.BookingEnd.SpecificMinutes,
                    bookingSlotsPolicy: JSON.parse(bookingPolicy.Booking_Policy).BookingSlots ?? {}
                });
            }
        }

        // if user updates bookingDate, start or end states, hide current set of spaces results until user has pressed search button again.
        if (prevState.bookingDate !== this.state.bookingDate || prevState.start !== this.state.start || prevState.end !== this.state.end)
        {
            this.setState({ showSpacesResults: false });
        }

        // if bookingDate is updated, startTime and endTime is also updated to be on the same date. 
        if (prevState.bookingDate !== this.state.bookingDate)
        {
            const bookingDate = DateTime.fromJSDate(this.state.bookingDate);
            const startTime = DateTime.fromJSDate(this.state.start);
            const endTime = DateTime.fromJSDate(this.state.end);

            const newStartTime = startTime.set({ year: bookingDate.year, month: bookingDate.month, day: bookingDate.day });
            const newEndTime = endTime.set({ year: bookingDate.year, month: bookingDate.month, day: bookingDate.day });

            this.setState({
                start: newStartTime.toJSDate(),
                end: newEndTime.toJSDate(),
            });
        }

    }

    private handleDateChange(event: Date): void
    {
        const selectedDate = new Date(event);
        this.setState({ bookingDate: selectedDate });
    }

    private startTimeChanged(time: Dayjs | null): void
    {
        const date = DateTime.fromJSDate(this.state.bookingDate).date();
        const startTime = ((time == null || !time.isValid()) ? DateHelper.null() : DateTime.fromISO(time.toISOString()).set({ year: date.year, month: date.month, day: date.day }));
        let endTime = (!this.state.isEndValid ? DateHelper.null() : DateTime.fromJSDate(this.state.end).set({ year: date.year, month: date.month, day: date.day }));

        if (startTime.isValid && endTime.isValid && startTime > endTime)
        {
            endTime = startTime.clone();
        }

        const duration = (startTime.isValid && endTime.isValid ? endTime.diff(startTime, ["hours", "minutes"]) : null);
        const durationAsString = (duration == null ? "-" : duration.durationToString());

        this.setState(
            {
                start: (startTime.isValid ? startTime.toJSDate() : this.state.start),
                end: (endTime.isValid ? endTime.toJSDate() : this.state.end),
                isStartValid: startTime.isValid,
                isEndValid: endTime.isValid,
                timeDifference: durationAsString,
            });
    }

    private endTimeChanged(time: Dayjs | null): void
    {
        const date = DateTime.fromJSDate(this.state.bookingDate).date();
        let startTime = (!this.state.isStartValid ? DateHelper.null() : DateTime.fromJSDate(this.state.start).set({ year: date.year, month: date.month, day: date.day }));
        const endTime = ((time == null || !time.isValid()) ? DateHelper.null() : DateTime.fromISO(time.toISOString()).set({ year: date.year, month: date.month, day: date.day }));

        if (startTime.isValid && endTime.isValid && startTime > endTime)
        {
            startTime = endTime.clone();
        }

        const duration = (startTime.isValid && endTime.isValid ? endTime.diff(startTime, ["hours", "minutes"]) : null);
        const durationAsString = (duration == null ? "-" : duration.durationToString());

        this.setState(
            {
                start: (startTime.isValid ? startTime.toJSDate() : this.state.start),
                end: (endTime.isValid ? endTime.toJSDate() : this.state.end),
                isStartValid: startTime.isValid,
                isEndValid: endTime.isValid,
                timeDifference: durationAsString,
            });
    }

    private async closeBooking(): Promise<void>
    {
        // close the swap and bookingCriteria, get new bookings data.

        // refresh bookings data
        await this.props.getBookings();
        // hide bookingCriteria slider.
        this.props.showBookingCriteria(false);
        this.setState({ isLoading: false, showChangeBookingDetailsPopup: false });
        this.props.setOpen(false);
    }

    private async modifyBooking(): Promise<void>
    {
        this.setState({ isLoading: true });

        if (this.state.moveResources === true && this.hasV2Rights)
        {
            //bookings patch only exists on v2, and if user elects not to moveResources, the change to booking is made via an update call.
            try
            {
                await this.props.patchBooking(this.state.selectedSpaceId, DateTime.fromJSDate(this.state.start), DateTime.fromJSDate(this.state.end));
            }
            catch
            {
                return;
            }
            finally
            {
                await this.closeBooking()
            }
        }
        else if (this.state.moveResources === true && !this.hasV2Rights)
        {
            try
            {
                await this.props.updateBooking(this.state.selectedSpaceId, this.state.start, this.state.end);
            }
            catch
            {
                return;
            }
            finally
            {
                await this.closeBooking();
            }
        }
        else
        {
            // additional steps to update a booking to handle NOT moving across a booking's resources and catering to a new space or time.
            try
            {
                await this.props.updateBooking(this.state.selectedSpaceId, this.state.start, this.state.end);
                await this.deleteEquipment(this.props.nodeId, this.props.bookingId);
            }
            catch
            {
                return;
            }
            finally
            {
                await this.closeBooking();
            }
        }
    }

    private async handleSubmit(): Promise<void>
    {
        if(this.interruptUpdateWithChangeBookingDetailsPopup)
            {
                this.showChangeBookingDetailsPopup(true);
                return;
            }

        await this.modifyBooking();
    }

    private showChangeBookingDetailsPopup(show: boolean): void
    {
        this.setState({showChangeBookingDetailsPopup: show});
    }

    private get interruptUpdateWithChangeBookingDetailsPopup(): boolean
    {
        if(this.props.bookingId==='' || this.props.cateringOrderIds.length == 0)
        {
            return false;
        }

        if(this.state.start === this.originalBookingStart)
        {
            return false;
        }

        return true;
    }

    private async deleteEquipment(nodeId: number, bookingId: string): Promise<void>
    {
        // delete booked equipment, e.g. hearing aids and presentation aids.     
        try
        {
            const bookingParties = await this.api.bookingParties.getByBookingId(nodeId, bookingId);
            const bookedEquipment = bookingParties.map(i => BookedEquipment.fromEquipment({ ...i, 'Floor_Name': this.nodes.Regions.flatMap(region => region.Buildings.flatMap(building => building.Floors.find(floor => floor.Node_Id === nodeId)?.Node_Name ?? ''))[0] }));
            this.setStateAsync({ bookedEquipment: bookedEquipment });
        }
        catch
        {
            return;
        }

        if (this.state.bookedEquipment.length === 0)
        {
            return;
        }

        const deleteEquipmentIdsPromises = this.state.bookedEquipment.map(i => this.api.bookingParties.delete(nodeId, i.recordId));
        const promiseAll = Promise.all(deleteEquipmentIdsPromises);

        try
        {
            await promiseAll;
        }
        catch
        {
            return;
        }
    }

    private async patchCateringOrder(payload: IPatchCateringOrder): Promise<void>
    {
        // if space has no catering, return out of function.
        if (!this.state.selectedSpaceHasCatering)
        {
            return;
        }

        try 
        {
            const filter = new CateringOrdersFilter(
                {
                    bookingId: this.props.bookingId
                });

            const order = await this.api.cateringOrders.getMany(this.props.buildingId, filter);
            if (order.length > 0)
            {
                // patch the booking order
                this.api.cateringOrders.patchCateringOrder(this.props.nodeId, order[0].Order_Id, payload);
            }
        }
        catch (error) 
        {
            return;
        }
    }

    private async searchSpaces(): Promise<void>
    {
        this.setState({
            showSpacesResults: true,
            selectedSpaceId: this.props.space.spaceId,
            selectedSpaceHasCatering: (this.props.space.cateringReqs == 1),
            selectedSpaceBookingPolicyId: this.props.space.bookingPolicyId,
        });
    }

    private closeChangeBookingTimePopup(): void
    {
        this.setState({ showChangeBookingDetailsPopup: false, isLoading: false });
    }

    public render(): JSX.Element
    {
        const props = this.props;
        const state = this.state;
        const bookingOnLinkedSpace = this.props.bookingSpaceId.includes(';');

        const userCanBookOutsideSlot = this.local.permissionAppliesToBuilding('API.Bookings.BookOutsidePolicySlots', this.props.buildingId);
        const userCanIgnoreMinMaxDuration = this.local.permissionAppliesToBuilding('API.Bookings.IgnoreMinMaxDuration', this.props.buildingId);

        return (
            <div className="flexMySearch-filter-criteria m-3 ml-5 mr-5">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: '3rem' }}>
                    <div className="flexMySearch-filter-criteria-title">
                        <Typography variant="h4" sx={{ fontFamily: 'Source Sans Pro', fontWeight: '700', mb: '0.5rem', color: (theme) => theme.palette.text.primary }}>
                            {/* {`Swap date and time`} */}
                            {this.labels.funcSwapTimeSwapDateAndTime_S}
                        </Typography>
                    </div>
                    <IbssSvgIcon cursor='pointer' fontSize='large' sx={{ color: (theme) => theme.palette.text.primary }} onClick={() => this.props.setOpen(false)}>
                        {Icons.RightArrowIcon}
                    </IbssSvgIcon>
                </Box>

                <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', mb: '1.5rem', color: (theme) => theme.palette.text.secondary }}>
                    {/* {'Original date for meeting is set to: '} */}
                    {`${this.labels.funcSwapTimeOriginalDateMeeting_S}: `}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'end', mb: '0.5rem' }}>
                    <SvgIcon component={SpacesIcon} inheritViewBox sx={{ height: '34px', width: '34px', marginRight: '1rem', color: (theme) => theme.palette.text.primary }} />
                    <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: '700', color: (theme) => theme.palette.text.primary }}>
                        {DateTime.fromJSDate(this.state.bookingDate).toLocaleDateString() ?? ''}
                    </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', color: (theme) => theme.palette.text.secondary }}>
                    {/* {'Curent Date'} */}
                    {this.labels.funcSwapTimeCurrentDate_S.toLocaleUpperCase()}
                </Typography>
                <Divider sx={{ mt: '1.5rem', mb: '1.5rem' }} />
                <Box>
                    {bookingOnLinkedSpace &&
                        <Box sx={{ padding: '0.8rem', mb: '1rem', backgroundColor: (theme) => theme.palette.error.light }} borderRadius={'8px'}>
                            <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', color: (theme) => theme.palette.error.main }}>
                                {this.labels.funcSwapSpaceLinkedWarning_L}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', color: (theme) => theme.palette.error.main }}>
                                {this.labels.funcSwapSpaceLinkedWarning_D}
                            </Typography>
                        </Box>
                    }
                    <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', mb: '1rem', color: (theme) => theme.palette.text.secondary }}>
                        {/* {`Provide a new date and time for the meeting and we will check if the current room, or other rooms are available for this slot which is able to accommodate the number of attendees selected.`} */}
                        {this.labels.funcSwapTimeSearchSpacesInstruction_D}
                    </Typography>

                    <IbssDatePicker
                        label={this.labels.HubLabelDate}
                        value={DateTime.fromJSDate(this.state.bookingDate)}
                        onChange={(event) => this.handleDateChange(event?.toJSDate() as Date)}
                        slotProps={{ textField: { fullWidth: true }, inputAdornment: { sx: { padding: '0px', marginLeft: '0px' } } }}
                        //disabled={updateBookingBtnDisable}
                        minDate={DateHelper.now()}
                        shouldDisableDate={date => BookingSlotHelper.isDateUnavailable(date as unknown as DateTime, this.state.bookingSlotsPolicy, userCanBookOutsideSlot)}
                    />

                    <Box component="div" sx={{ display: 'flex', justifyContent: 'space-between', mt: '1rem', mb: '1rem', gap: '0.5rem' }}>
                        <Box style={{ alignSelf: 'center' }}>
                            <IbssTimePicker
                                shouldDisableTime={(time, type) => BookingSlotHelper.isTimeUnavailable(time.toJSDate(), type, 'start', this.state.bookingDate, this.state.start, this.state.end, this.state.bookingSlotsPolicy, userCanBookOutsideSlot, userCanIgnoreMinMaxDuration)}
                                // className="inputboxmodel-date"
                                label={this.labels.HubLabelFrom}
                                value={DateTime.fromJSDate(this.state.start)}
                                onChange={time => this.startTimeChanged(dayjs(time?.toJSDate()) as (Dayjs | null))}
                                ampm={false}
                                minutesStep={BookingSlotHelper.getMinutesStepFromPolicy(this.state.startIntervalMinutes)}
                                slotProps={{ textField: { error: false } }}
                                //disabled={updateBookingBtnDisable}
                            />
                        </Box>
                        <Box style={{ alignSelf: 'center' }}>
                            <IbssTimePicker
                                shouldDisableTime={(time, type) => BookingSlotHelper.isTimeUnavailable(time.toJSDate(), type, 'end', this.state.bookingDate, this.state.start, this.state.end, this.state.bookingSlotsPolicy, userCanBookOutsideSlot, userCanIgnoreMinMaxDuration)}
                                //className="inputboxmodel-date"
                                label={this.labels.HubLabelTo}
                                value={DateTime.fromJSDate(this.state.end)}
                                onChange={time => this.endTimeChanged(dayjs(time?.toJSDate()) as (Dayjs | null))}
                                ampm={false}
                                minutesStep={BookingSlotHelper.getMinutesStepFromPolicy(this.state.endIntervalMinutes)}
                                slotProps={{ textField: { error: false } }}
                                // disabled={updateBookingBtnDisable}
                            />
                        </Box>
                    </Box>

                    <IbssButton
                        variant={'contained'}
                        fullWidth
                        sx={{ mt: '1rem', mb: '1rem', padding: '0.75rem', backgroundColor: (theme) => theme.palette.grey[400], color: (theme) => theme.palette.text.primary }}
                        onClick={async () => await this.searchSpaces()}
                        disabled={this.state.isLoading}
                    >
                        <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold' }}>
                            {this.labels.HubLabelSearch}
                        </Typography>
                    </IbssButton>
                    {
                        this.state.showSpacesResults && <>
                            <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', color: (theme) => theme.palette.text.secondary }}>
                                {/* {`The following spaces with matching criteria have been found`} */}
                                {this.labels.funcSwapTimeSpacesResults_L}
                            </Typography>
                            <FormControl sx={{ width: '100%' }}>
                                <SpacePicker
                                    buildingId={props.buildingId}
                                    floorId={props.space.floorId}
                                    spaceTypeId={props.space.spaceType}
                                    minCapacity={TypeHelper.toNumber(props.space.capacity) ?? 0}
                                    lowestMinCapacity={2}
                                    requiresCatering={TypeHelper.toBoolean(props.space.cateringReqs) ?? false}
                                    requiresAudioVisual={TypeHelper.toBoolean(props.space.requiresAV) ?? false}
                                    requiresPresentationAids={TypeHelper.toBoolean(props.space.presentationAidReqs) ?? false}
                                    requiresHearingAids={TypeHelper.toBoolean(props.space.hearingAidReqs) ?? false}
                                    selectedSpaceId={state.selectedSpaceId}
                                    availableFrom={DateTime.fromJSDate(state.start)}
                                    availableTo={DateTime.fromJSDate(state.end)}
                                    onChange={i => this.setState({ selectedSpaceId: i.id, selectedSpaceHasCatering: i.hasCatering, selectedSpaceBookingPolicyId: i.bookingPolicyId })}
                                />
                            </FormControl>
                        </>
                    }
                </Box>
                <Box sx={{ mt: '1rem', mb: '1rem' }}>
                    <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', color: (theme) => theme.palette.text.secondary }}>
                        {/* {'By default all existing tasks associated with this meeting will be moved to the new space. To not do this and cancel all existing tasks, uncheck the button below.'} */}
                        {this.labels.funcSwapTimeMoveResourcesInstruction_D}
                    </Typography>
                </Box>

                <Box sx={{ mt: '1rem', mb: '1rem' }}>
                    <FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={this.state.moveResources}
                                    onChange={() => this.setState({ moveResources: !this.state.moveResources })}
                                />
                            }
                            label={
                                <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold', color: (theme) => theme.palette.text.secondary }}>
                                    {/* {'Move resources and catering orders to new location'} */}
                                    {this.labels.funcSwapTimeMoveResources_L}
                                </Typography>
                            }
                        />
                    </FormControl>
                </Box>

                <IbssButton
                    variant={'contained'}
                    fullWidth
                    size={'large'}
                    sx={{ mt: '1rem', padding: '0.75rem' }}
                    onClick={() => this.handleSubmit()}
                    //disable confirm swapDateTime button if the Search button has not been pressed. if component is loading or if for the same spaceId, booking date, start and end times have not bee updated.
                    disabled={!this.state.showSpacesResults || this.state.isLoading}
                >
                    <Typography sx={{ fontFamily: 'Source Sans Pro', fontWeight: 'bold' }}>
                        {/* {'Confirm Space Swap'} */}
                        {this.labels.funcSwapSpaceSwap_S}
                    </Typography>
                </IbssButton>
                
                <ChangeBookingDetailsDialog
                    show={this.state.showChangeBookingDetailsPopup}
                    onClose={()=> this.closeChangeBookingTimePopup()}
                    displayLocation={IBookingDetailsApplication.Onelens}
                    buildingId={this.props.buildingId}
                    isSwappingSpace={this.state.selectedSpaceId !== this.props.bookingSpaceId}
                    nodeId={this.props.nodeId}
                    spaceId={this.state.selectedSpaceId}
                    updateBooking={()=> this.modifyBooking()}
                    bookingStart={this.state.start}
                    bookingEnd={this.state.end}
                    originalBookingStart={this.originalBookingStart}
                    originalBookingEnd={this.originalBookingEnd}
                    originalSpaceId={this.props.bookingSpaceId}
                    cateringOrderIds={this.props.cateringOrderIds}
                    alwaysApproveCateringOrders={this.state.buildingAlwaysApproveCateringOrders}
                />
            </div>
        )
    }
}

export default SwapDateTime;

export interface IProps
{
    open: boolean;
    bookingStart: string; // ISO compliant string representations of UTC time, passed from a get bookings call made in scheduleView.
    bookingEnd: string;
    bookingId: string;
    bookingSpaceId: string;
    bookingSlotsPolicy: IBookingSlots;
    startIntervalMinutes: string[];
    endIntervalMinutes: string[];
    buildingId: number;
    nodeId: number;
    space: IMeetingSpace;
    cateringOrderIds: string[];
    setOpen: (val: boolean) => void;
    updateBooking: (spaceId: string, startTime: Date, endTime: Date) => Promise<void>;
    patchBooking: (spaceId: string, startTime: DateTime, endTime: DateTime) => Promise<void>;
    showBookingCriteria: (val: boolean) => void;
    getBookings: () => Promise<void>;
}

export interface IState
{
    bookingDate: Date;
    start: Date;
    end: Date;
    bookingSlotsPolicy: IBookingSlots;
    startIntervalMinutes: string[];
    endIntervalMinutes: string[];
    isStartValid: boolean;
    isEndValid: boolean;
    timeDifference: string;
    isLoading: boolean;
    selectedSpaceId: string;
    selectedSpaceHasCatering: boolean;
    selectedSpaceBookingPolicyId: string;
    bookedEquipment: BookedEquipment[];
    moveResources: boolean;
    showChangeBookingDetailsPopup: boolean;
    showSpacesResults: boolean;
    buildingAlwaysApproveCateringOrders: number;
}

export interface IBookedEquipment
{
    Booking_End: string;
    Booking_Id: string;
    Booking_Participant_CheckedIn: number;
    Booking_Participant_Email: string;
    Booking_Participant_Name: string;
    Booking_Participant_Organisation: string;
    Booking_Participant_Type: number;
    Booking_Resource_Id: string;
    Booking_Start: string;
    Booking_Visitor: number;
    Node_Id: number;
    Record_Id: string;
    Floor_Name: string;
}

export class BookedEquipment
{
    public bookingEnd = '';
    public bookingId = '';
    public bookingParticipantCheckedIn = 0;
    public bookingParticipantEmail = '';
    public bookingParticipantName = '';
    public bookingParticipantOrganisation = '';
    public bookingParticipantType = 0;
    public bookingResourceId = '';
    public bookingStart = '';
    public bookingVisitor = 0;
    public nodeId = 0;
    public recordId = '';
    public floorName = '';

    public static fromEquipment(equipment: IBookedEquipment): BookedEquipment
    {
        return {
            bookingEnd: equipment.Booking_End,
            bookingId: equipment.Booking_Id,
            bookingParticipantCheckedIn: equipment.Booking_Participant_CheckedIn,
            bookingParticipantEmail: equipment.Booking_Participant_Email,
            bookingParticipantName: equipment.Booking_Participant_Name,
            bookingParticipantOrganisation: equipment.Booking_Participant_Organisation,
            bookingParticipantType: equipment.Booking_Participant_Type,
            bookingResourceId: equipment.Booking_Resource_Id,
            bookingStart: equipment.Booking_Start,
            bookingVisitor: equipment.Booking_Visitor,
            nodeId: equipment.Node_Id,
            recordId: equipment.Record_Id,
            floorName: equipment.Floor_Name,
        };
    }
}